<template>
  <div>
    <player-gvnts-table
      v-if="isGvntsTableVisible"
      :gvnts="gvnts"
      @gvnt-finish-requested="finishGvntWithConfirmation"
    />

    <p v-if="message !== null">
      {{message}}
    </p>

    <loading-indicator v-if="isLoading" />
  </div>
</template>

<script>
import { HTTP } from '../http-common.js';
import notifier from '../notifier.js';

import { getGvntTypeName } from '../model/gvnt.js';

/**
 * @typedef { import('../service/confirm.js').ConfirmHandle } ConfirmHandle
 */
import { confirmAction } from '../service/confirm.js';

import LoadingIndicator from './LoadingIndicator.vue';
import PlayerGvntsTable from './PlayerGvntsTable.vue';

const LOADING_STATUS__INIT    = 1;
const LOADING_STATUS__LOADING = 2;
const LOADING_STATUS__NORMAL  = 3;
const LOADING_STATUS__ERROR   = 4;

export default {
  name: 'PlayerGvnts',

  components: {
    LoadingIndicator,
    PlayerGvntsTable,
  },

  props: {
    playerId: {
      type: Number,
      required: true,
    }
  },

  data() {
    return {
      loadingStatus: LOADING_STATUS__INIT,
      gvnts: null,
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadingStatus === LOADING_STATUS__LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadingStatus === LOADING_STATUS__NORMAL;
    },

    /** @returns {?string} */
    message() {
      if(!this.isLoadedNormal) {
        return null;
      }

      const gvnts = this.gvnts;
      if(gvnts === null) {
        return null;
      }

      if(gvnts.length === 0) {
        return 'Этот игрок не участвовал в игровых событиях.';
      }

      return null;
    },

    /** @returns {boolean} */
    isGvntsTableVisible() {
      if(!this.isLoadedNormal) {
        return false;
      }

      const gvnts = this.gvnts;
      if(gvnts === null) {
        return false;
      }

      return gvnts.length > 0;
    },
  },

  created() {
    this.loadGvnts();
  },

  methods: {
    loadGvnts() {
      notifier.deleteSenderNotifications(this);

      this.loadingStatus = LOADING_STATUS__LOADING;

      const url = `/player/${this.playerId}/gvnts`;
      HTTP.get(url)
      .then(response => {
        const gvnts = response.data.data;
        gvnts.sort(comparePlayerGvnts);

        this.gvnts = gvnts;
        this.loadingStatus = LOADING_STATUS__NORMAL;
      })
      .catch(err => {
        this.loadingStatus = LOADING_STATUS__ERROR;
        notifier.showError(err, {sender: this});
      });
    },

    //-------------------------------------------------------------------------
    // Finish
    //-------------------------------------------------------------------------

    finishGvntWithConfirmation(gvnt) {
      notifier.deleteSenderNotifications(this);

      this.confirmGvntFinish(
        gvnt,
        () => {
          this.finishGvnt(gvnt);
        },
      );
    },

    /**
     * @param {Object} gvnt
     * @param {ConfirmHandle} onConfirmed
     */
    confirmGvntFinish(gvnt, onConfirmed) {
      const gvntId = gvnt.gvntId;
      const gvntTypeName = getGvntTypeName(gvnt.gvntType);
      const playerId = this.playerId;

      const messageTemplate = `Are you sure you want to finish game event ${gvntId} (${gvntTypeName}) for player ${playerId} {env}?`;

      confirmAction(
        this,
        messageTemplate,
        'Finish',
        onConfirmed,
      );
    },

    finishGvnt(gvnt) {
      const gvntId = gvnt.gvntId;
      const playerId = this.playerId;
      const url = `/gvnt/${gvntId}/player/${playerId}/finalize`;

      HTTP.post(url)
      .then((response) => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        gvnt.ftime = data.ftime;
        notifier.showSuccess('Game event finished for this player.');
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },

    //-------------------------------------------------------------------------
  },
}

function comparePlayerGvnts(gvntA, gvntB) {
  if(gvntA.stime !== gvntB.stime) {
    return gvntB.stime - gvntA.stime;
  }

  if(gvntA.srtime !== gvntB.srtime) {
    return gvntB.srtime - gvntA.srtime;
  }

  return gvntB.gvntId - gvntA.gvntId;
}
</script>
